// QROS statement-tag wire types, shared by the entity + ontology hooks and
// the TagEditor component. QROS-native only — the RDF backend never emits or
// consumes these. Mirrors TagDto / TagView in tres-api/src/handlers/entity.rs.

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './api';
import { useApiSession } from './useApiSession';
import type { TermInput } from './useEntity';

export const TAG_PREFIX = '@';

/** QROS models a literal's language as the `@language` statement tag, not as a
 *  term qualifier — the substrate drops `lang` off the object value, so an
 *  editor's language box means nothing unless it is carried across as this tag.
 *  Returns nothing when the draft already declares `@language`, so an explicit
 *  tag row wins over the box. */
export function languageTag(lang: string | undefined, drafted: TagInput[]): TagInput[] {
  const value = lang?.trim();
  if (!value || drafted.some((t) => t.name === '@language')) return [];
  return [{ name: '@language', value: { value } }];
}

/** Write-side tag: an `@`-name plus an object-shaped value (uri or literal). */
export interface TagInput {
  name: string;
  value: TermInput;
}

/** Read-side resolved tag value — a URI carries a display label. */
export type TagValueView =
  | { kind: 'uri'; uri: string; label: string }
  | { kind: 'literal'; value: string; lang?: string; datatype?: string };

/** Read-side tag: an `@`-name plus a display-ready value. */
export interface TagView {
  name: string;
  value: TagValueView;
}

// ---- Tag catalog (the three-tier model, Ch14 §14.2a) ----
//
// Tier 1 = builtin catalog (server-side compile-time list), tier 2 =
// ontology-declared `:Tag` entities + the predicate's required/allowed
// contract, tier 3 (free-form) is purely client-side. RDF backends return
// empty tiers.

export interface CatalogTag {
  name: string;
  /** Value kind driving the typed control: text | number | date | entity | flag. */
  kind?: string | null;
  doc?: string;
  label?: string | null;
  uri?: string;
}

export interface TagCatalogResponse {
  builtin: CatalogTag[];
  declared: CatalogTag[];
  required: string[];
  allowed: string[];
}

export function useTagCatalog(graph: string | null | undefined, predicate?: string | null) {
  const { token, ready } = useApiSession();
  return useQuery<TagCatalogResponse>({
    queryKey: ['tags', 'catalog', graph ?? '', predicate ?? ''],
    enabled: ready && !!graph,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const params = new URLSearchParams({ graph: graph as string });
      if (predicate) params.set('predicate', predicate);
      const r = await apiFetch<TagCatalogResponse>(
        `/api/v1/tags/catalog?${params.toString()}`,
        token,
      );
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}
